import { Component, ViewChild, ElementRef, input, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { EventConfirmationDialogComponent } from 'src/app/shared/calendar/event-confirmation-dialog/event-confirmation-dialog.component';
import { CalendarComponent } from 'src/app/shared/calendar/calendar.component';
import { MaterialModule } from 'src/app/material.module';

// Update the TimeSlot interface to include an error message
interface TimeSlot {
  startTime: string;
  endTime: string;
  isValid?: boolean;
  errorMessage?: string;
}

interface DayAvailability {
  day: string;
  timeSlots: TimeSlot[];
}

@Component({
  selector: 'app-general-meeting-scheduler',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  templateUrl: './general-meeting-scheduler.component.html',
  styleUrl: './general-meeting-scheduler.component.scss'
})
export class GeneralMeetingSchedulerComponent {
  // Add ViewChild references to the template input elements
  @ViewChild('startTimeInput') startTimeInput!: ElementRef;
  @ViewChild('endTimeInput') endTimeInput!: ElementRef;

  // Meeting duration options
  durations: any = [
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 45, label: '45 min' },
    { value: 60, label: '1 h' },
    { value: 90, label: '1.5 h' },
    { value: 120, label: '2 h' },
    // { value: 'custom', label: 'Custom' }
  ];
  selectedDuration = 60; // Default to 1h

  // Availability period
  startDate: any;
  endDate: any;

  // Days of the week
  daysOfWeek = [
    'Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'
  ];

  // Day availability
  availability: DayAvailability[] = [
    {
      day: 'Sundays',
      timeSlots: []
    },
    { day: 'Mondays', timeSlots: [] },
    { day: 'Tuesdays', timeSlots: [] },
    { day: 'Wednesdays', timeSlots: [] },
    { day: 'Thursdays', timeSlots: [] },
    { day: 'Fridays', timeSlots: [] },
    { day: 'Saturdays', timeSlots: [] }
  ];

  // Buffer settings
  bufferTimeBefore: any ="0";
  bufferTimeAfter: any ="0";
  shortestNotice: any ="0";
  bookingFuturePeriod = '365';
  timeSlotIncrement = '15';

  // UI state
  showBufferSettings = false;
  editingTimeSlot: { dayIndex: number; slotIndex: number } | null = null;
  timeFormat24h = false;

  isCreatingNewSlot = false;
  tempSlot: TimeSlot = { startTime: '', endTime: '' };

  generalMeetingData = input<any>()
  editMode = input<boolean>();

  constructor(){
    effect(()=>{
      if(this.generalMeetingData()){
        this.availability = this.generalMeetingData().availability;
        this.selectedDuration =  this.generalMeetingData().duration;
        this.startDate = this.generalMeetingData().startDate;
        this.endDate = this.generalMeetingData().endDate;
        this.bufferTimeBefore = this.generalMeetingData().bufferSettings.bufferTimeBefore;
        this.bufferTimeAfter = this.generalMeetingData().bufferSettings.bufferTimeAfter;
        this.shortestNotice = this.generalMeetingData().bufferSettings.shortestNotice;
        this.bookingFuturePeriod = this.generalMeetingData().bufferSettings.bookingFuturePeriod;
        this.timeSlotIncrement = this.generalMeetingData().bufferSettings.timeSlotIncrement;
      }
    })
  }

  ngOnInit(){
    
    
  }

  // Methods
  selectDuration(duration: number): void {
    this.selectedDuration = duration;
  }

  toggleBufferSettings(): void {
    this.showBufferSettings = !this.showBufferSettings;
  }

  addTimeSlot(day: string): void {
    const dayIndex = this.availability.findIndex(d => d.day === day);
    if (dayIndex !== -1) {
      // Open the time slot editor for adding a new slot
      this.openTimeSlotEditor(dayIndex);
    }
  }
  
  openTimeSlotEditor(dayIndex: number, slotIndex?: number): void {
    if (slotIndex === undefined) {
      // We're creating a new slot, not editing an existing one
      this.isCreatingNewSlot = true;
      
      // Initialize with empty values
      this.tempSlot = { 
        startTime: '', 
        endTime: '',
        isValid: false,
        errorMessage: 'Please set start and end times'
      };
      
      // Set editing to the day index
      this.editingTimeSlot = { dayIndex, slotIndex: -1 };
      
      // Validate the temp slot for potential overlaps
      this.validateTempTimeSlot();
    } else {
      // We're editing an existing slot
      this.isCreatingNewSlot = false;
      this.editingTimeSlot = { dayIndex, slotIndex };
    }
  }
  
  closeTimeSlotEditor(): void {
    this.editingTimeSlot = null;
    this.isCreatingNewSlot = false;
    // Reset the temporary slot
    this.tempSlot = { startTime: '', endTime: '', isValid: true, errorMessage: undefined };
  }
  
  saveTimeSlot(): void {
    if (!this.editingTimeSlot) {
      return;
    }
    
    const { dayIndex } = this.editingTimeSlot;
    
    if (this.isCreatingNewSlot) {
      // For new slots, add the temporary slot to the day's timeSlots array
      if (this.tempSlot.isValid) {
        this.availability[dayIndex].timeSlots.push({
          startTime: this.tempSlot.startTime,
          endTime: this.tempSlot.endTime,
          isValid: true
        });
        this.isCreatingNewSlot = false;
        this.closeTimeSlotEditor();
      }
    } else {
      // For existing slots, just validate and close if valid
      const slot = this.availability[dayIndex].timeSlots[this.editingTimeSlot.slotIndex];
      if (slot.isValid) {
        this.closeTimeSlotEditor();
      }
    }
  }
  
  isValidTimeFormat(timeString: string): boolean {
    // Regular expressions for 12-hour and 24-hour time formats
    const regex12h = /^(1[0-2]|0?[1-9]):[0-5][0-9]\s?(AM|PM|am|pm)$/;
    const regex24h = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    
    return this.timeFormat24h ? regex24h.test(timeString) : regex12h.test(timeString);
  }
  
  parseTimeString(timeString: string): number {
    if (this.timeFormat24h) {
      const [hours, minutes] = timeString.split(':').map(Number);
      return hours * 60 + minutes;
    } else {
      const isPM = timeString.toLowerCase().includes('pm');
      const [time] = timeString.split(/\s/);
      const [hours, minutes] = time.split(':').map(Number);
      const adjustedHours = isPM && hours !== 12 ? hours + 12 : (!isPM && hours === 12 ? 0 : hours);
      return adjustedHours * 60 + minutes;
    }
  }
  
  formatTime(date: Date): string {
    if (this.timeFormat24h) {
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    } else {
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const period = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // Convert 0 to 12 for 12-hour format
      return `${hours}:${String(minutes).padStart(2, '0')} ${period}`;
    }
  }
  
  toggleTimeFormat(): void {
    this.timeFormat24h = !this.timeFormat24h;
    
    // Convert all existing time slots to the new format
    this.availability.forEach(day => {
      day.timeSlots.forEach(slot => {
        if (this.isValidTimeFormat(slot.startTime) && this.isValidTimeFormat(slot.endTime)) {
          const startDate = new Date();
          const endDate = new Date();
          
          // Parse and set the start time
          const startMinutes = this.parseTimeString(slot.startTime);
          startDate.setHours(Math.floor(startMinutes / 60));
          startDate.setMinutes(startMinutes % 60);
          
          // Parse and set the end time
          const endMinutes = this.parseTimeString(slot.endTime);
          endDate.setHours(Math.floor(endMinutes / 60));
          endDate.setMinutes(endMinutes % 60);
          
          // Format the times based on the current format setting
          slot.startTime = this.formatTime(startDate);
          slot.endTime = this.formatTime(endDate);
        }
      });
    });
  }

  // Native time input helper methods
  convertToTimeInputFormat(timeString: string): string {
    try {
      // Convert from "9:00 AM" format to "09:00" format needed by time input
      const isPM = timeString.toLowerCase().includes('pm');
      const [time] = timeString.split(/\s/);
      const [hours, minutes] = time.split(':').map(Number);
      
      let adjustedHours = hours;
      if (isPM && hours !== 12) {
        adjustedHours = hours + 12;
      } else if (!isPM && hours === 12) {
        adjustedHours = 0;
      }
      
      return `${String(adjustedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    } catch (e) {
      // Return a default value if parsing fails
      return '09:00';
    }
  }

  convertFromTimeInputFormat(timeValue: string): string {
    try {
      const [hours, minutes] = timeValue.split(':').map(Number);
      
      if (this.timeFormat24h) {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      } else {
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12-hour format
        return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
      }
    } catch (e) {
      return timeValue;
    }
  }

  onStartTimeChange(newValue: string): void {
    if (this.editingTimeSlot) {
      if (this.isCreatingNewSlot) {
        // Update the temporary slot
        this.tempSlot.startTime = this.convertFromTimeInputFormat(newValue);
        this.validateTempTimeSlot();
      } else {
        // Update an existing slot
        const { dayIndex, slotIndex } = this.editingTimeSlot;
        this.availability[dayIndex].timeSlots[slotIndex].startTime = this.convertFromTimeInputFormat(newValue);
        this.validateTimeSlot();
      }
    }
  }
  
  onEndTimeChange(newValue: string): void {
    if (this.editingTimeSlot) {
      if (this.isCreatingNewSlot) {
        // Update the temporary slot
        this.tempSlot.endTime = this.convertFromTimeInputFormat(newValue);
        this.validateTempTimeSlot();
      } else {
        // Update an existing slot
        const { dayIndex, slotIndex } = this.editingTimeSlot;
        this.availability[dayIndex].timeSlots[slotIndex].endTime = this.convertFromTimeInputFormat(newValue);
        this.validateTimeSlot();
      }
    }
  }

  validateTempTimeSlot(): void {
    if (!this.editingTimeSlot) return;
    
    const startMinutes = this.parseTimeString(this.tempSlot.startTime);
    const endMinutes = this.parseTimeString(this.tempSlot.endTime);
    
    // First check if end time is after start time
    const isTimeValid = endMinutes > startMinutes;
    
    // Then check for overlaps with existing slots
    const hasOverlap = this.isTimeSlotOverlapping(
      this.editingTimeSlot.dayIndex,
      this.tempSlot.startTime,
      this.tempSlot.endTime
    );
    
    // Set isValid to true only if the time is valid AND there's no overlap
    this.tempSlot.isValid = isTimeValid && !hasOverlap;
    
    // Set error message based on validation results
    if (!isTimeValid) {
      this.tempSlot.errorMessage = "End time must be after start time";
    } else if (hasOverlap) {
      this.tempSlot.errorMessage = "This time slot overlaps with an existing slot";
    } else {
      this.tempSlot.errorMessage = undefined;
    }
  }
  
  validateTimeSlot(): void {
    if (!this.editingTimeSlot) return;
    
    const { dayIndex, slotIndex } = this.editingTimeSlot;
    const slot = this.availability[dayIndex].timeSlots[slotIndex];
    
    const startMinutes = this.parseTimeString(slot.startTime);
    const endMinutes = this.parseTimeString(slot.endTime);
    
    // First check if end time is after start time
    const isTimeValid = endMinutes > startMinutes;
    
    // Then check for overlaps with existing slots (excluding the current slot)
    const hasOverlap = this.isTimeSlotOverlapping(
      dayIndex,
      slot.startTime,
      slot.endTime,
      slotIndex
    );
    
    // Set isValid to true only if the time is valid AND there's no overlap
    slot.isValid = isTimeValid && !hasOverlap;
    
    // Set error message based on validation results
    if (!isTimeValid) {
      slot.errorMessage = "End time must be after start time";
    } else if (hasOverlap) {
      slot.errorMessage = "This time slot overlaps with an existing slot";
    } else {
      slot.errorMessage = undefined;
    }
  }
  

  
  setTimePresetWithConversion(startTime: string, endTime: string): void {
    if (!this.editingTimeSlot) return;
    
    if (this.isCreatingNewSlot) {
      // Update the temporary slot
      this.tempSlot.startTime = startTime;
      this.tempSlot.endTime = endTime;
      this.validateTempTimeSlot();
    } else {
      // Update an existing slot
      const { dayIndex, slotIndex } = this.editingTimeSlot;
      
      // Update the model directly
      this.availability[dayIndex].timeSlots[slotIndex].startTime = startTime;
      this.availability[dayIndex].timeSlots[slotIndex].endTime = endTime;
      
      // Validate the time slot
      this.validateTimeSlot();
    }
  }

  // Add this method to check for time slot overlaps
  isTimeSlotOverlapping(dayIndex: number, startTime: string, endTime: string, excludeSlotIndex?: number): boolean {
    const startMinutes = this.parseTimeString(startTime);
    const endMinutes = this.parseTimeString(endTime);
    
    return this.availability[dayIndex].timeSlots.some((slot, index) => {
      // Skip the current slot being edited
      if (excludeSlotIndex !== undefined && index === excludeSlotIndex) {
        return false;
      }
      
      const slotStartMinutes = this.parseTimeString(slot.startTime);
      const slotEndMinutes = this.parseTimeString(slot.endTime);
      
      // Check for overlap
      // Overlap occurs when:
      // - New start time is between existing slot's start and end times
      // - New end time is between existing slot's start and end times
      // - New slot completely contains the existing slot
      const newStartInExisting = startMinutes >= slotStartMinutes && startMinutes < slotEndMinutes;
      const newEndInExisting = endMinutes > slotStartMinutes && endMinutes <= slotEndMinutes;
      const newContainsExisting = startMinutes <= slotStartMinutes && endMinutes >= slotEndMinutes;
      
      return newStartInExisting || newEndInExisting || newContainsExisting;
    });
  }

  removeTimeSlot(day: string, index: number): void {
    const dayIndex = this.availability.findIndex(d => d.day === day);
    if (dayIndex !== -1) {
      this.availability[dayIndex].timeSlots.splice(index, 1);
    }
  }

  saveAvailability() {
    // Implementation for saving availability
    return {
      duration: this.selectedDuration,
      startDate: this.startDate,
      endDate: this.endDate,
      availability: this.availability,
      bufferSettings: {
        bufferTimeBefore: this.bufferTimeBefore,
        bufferTimeAfter: this.bufferTimeAfter,
        shortestNotice: this.shortestNotice,
        bookingFuturePeriod: this.bookingFuturePeriod,
        timeSlotIncrement: this.timeSlotIncrement
      }
    }
  }
}